<template>
  <Sidebar v-model:visible="isVisible" position="right" :style="{ width: sidebarWidth + 'px' }">
    <template #header>
      <div class="flex justify-between items-center w-full">
        <h3 class="ai-font">Hello, how can I help?</h3>
      </div>
    </template>

    <div class="sidebar-content">
      <div class="chat-threads" v-if="chatHistory.length > 0 || !waitingForChatResponse">
        <div v-for="(item, index) in chatHistory" :key="index">
          <div class="user-prompts">
            <span class="icon"><i class="ai-font icon pi pi-user"></i></span>
            <span>{{ item.user }}</span>
          </div>
          <div class="ai-bot-response" v-if="item.model">
            <span class="icon"><i class="ai-font icon pi pi-microchip-ai"></i></span>
            <div class="marked" v-html="marked(item.model, {})"></div>
          </div>
        </div>
      </div>

      <div class="loading-skeleton" v-if="waitingForChatResponse">
        <Skeleton width="100%" class="mb-2"></Skeleton>
        <Skeleton width="75%" class="mb-2"></Skeleton>
        <Skeleton width="50%" class="mb-2"></Skeleton>
      </div>
    </div>

    <div class="resize-handle" @mousedown="startResize"></div>

    <div class="sidebar-footer">
      <Textarea
          v-model="customUserPrompt"
          autoResize
          placeholder="Enter a prompt here"
          class="ai-prompt no-resize"
          :style="{ width: textAreaWidth }"
          @keydown.enter.exact.prevent="handleEnter" />
    </div>
  </Sidebar>
</template>

<script setup>
import {computed, defineEmits, defineProps, nextTick, onMounted, ref} from "vue";
import {storeToRefs} from "pinia";
import {useWidgetStore, useCustomerStore} from "@/store/index.ts";
import useMicroservice from '@/hooks/useMicroservice'
import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue";
import { marked } from "marked";

const {fetchAiDataForButton} = useMicroservice()
const {selectedWidget} = storeToRefs(useWidgetStore())

const {getComputedFormulaSummary, getComputedHasFormula, getComputedAssociatedRoots, getComputedTextByHookId} = useComputedTreeValue()

const props = defineProps({
  visible: Boolean,
  elementData: Object
})

const customUserPrompt = ref('')
const customerStore = useCustomerStore();
const chatHistory = computed(() => customerStore.getChatHistory(props.elementData.id))
const waitingForChatResponse = ref(false)
const initialMessageSent = ref(false)

const sidebarWidth = ref(400)
const textAreaWidth = computed(() => sidebarWidth.value - 50 + 'px')
const isResizing = ref(false)

const emit = defineEmits(["update:visible"])

const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit("update:visible", value),
})

onMounted(() => {
  const existingChat = customerStore.getChatHistory(props.elementData.id)
  if (existingChat.length === 0 && !initialMessageSent.value) {
    handleEnter()
    nextTick(() => (initialMessageSent.value = true))
  }
})

const handleEnter = async () => {
  waitingForChatResponse.value = true

  const hookId = props.elementData.type !== 'chart' ? props.elementData.hookId : null
  const variableName = props.elementData.type !== 'chart' ? getComputedTextByHookId(hookId) : null
  const userPrompt = customUserPrompt.value.length > 0
      ? customUserPrompt.value
      : props.elementData.type === 'chart'
          ? 'Provide a summary of this chart'
          : `Provide a summary of ${variableName}`
  customUserPrompt.value = ''

  const data = await generatePrompt(variableName, userPrompt)

  customerStore.addMessage(props.elementData.id, userPrompt, data)

  waitingForChatResponse.value = false
}

const generatePrompt = async (variableName, userPrompt) => {
  const hookId = props.elementData.hookId
  if (initialMessageSent.value) {
    const chatHistory = customerStore.getChatHistory(props.elementData.id)
    return await fetchAiDataForButton({ category: 'userPrompt', customPrompt: userPrompt, chatHistory })
  } else if (props.elementData.type === 'chart') {
    const data = formatChartData()
    return await fetchAiDataForButton({ category: 'chart', formulaSummary: data })
  } else if (props.elementData.aiButtonIncludeFormulas && (isValueDriver(hookId) || getComputedHasFormula(hookId))) {
    return await fetchAiDataForButton({ category: 'hasFormula', variableName, formulaSummary: getComputedFormulaSummary(hookId) })
  } else {
    const associatedRoots = getComputedAssociatedRoots(hookId)
    return await fetchAiDataForButton({ variableName, associatedRoots, formulaSummary: getComputedFormulaSummary(hookId) })
  }
}

//TODO: better way of detecting it's a value driver? lol
const isValueDriver = (hookId) => {
  return hookId.includes('auto-vd')
}

const formatChartData = () => {
  //? Are there other elements of chart data that could help paint the picture better for AI
  const chartType = getChartType()
  const chartData = props.elementData.data.series
      .map(row => row.filter(value => value !== null && value !== ""))
      .filter(row => row.length > 0)
  const categories = chartType === 'stacked bar' ? getStackedBarCategories(chartData) : null

  return {
    chartType,
    categories,
    chartData
  }
}

const getChartType = () => {
  const { type, options } = props.elementData

  if (options.stackBars) return 'stacked bar'

  if (type !== 'line' && type !== 'pie' && options.horizontalBars) return 'bar'

  if (type === 'line') {
    if (options.area) return 'area'
    if (options.scatter) return 'scatter'
    if (options.smooth) return 'spline'
  }

  if (type === 'column') {
    if (options.waterfall) return 'waterfall'
  }

  return type
}

const  getStackedBarCategories = (chartData) => {
  const number = chartData[0].length - 1 //removes the first empty column
  const categories = []

  for (let i = 1; i <= number; i++) {
    categories.push(props.elementData.jspreadsheet.cols[i].title)
  }
  return categories
}

const startResize = () => {
  isResizing.value = true

  // temporarily adding class to stop text highlighting as we resize the screen
  document.body.classList.add("no-select")

  document.addEventListener("mousemove", resize)
  document.addEventListener("mouseup", stopResize)
}

const resize = (event) => {
  if (isResizing.value) {
    // need to get width of screen first since sidebar sits on right side of screen
    const screenWidth = window.innerWidth
    const newWidth = screenWidth - event.clientX
    if (newWidth > 400 && newWidth < 1400) {
      sidebarWidth.value = newWidth
    }
  }
}

const stopResize = () => {
  isResizing.value = false

  document.body.classList.remove("no-select")

  document.removeEventListener("mousemove", resize)
  document.removeEventListener("mouseup", stopResize)
}

</script>

<style lang="scss" scoped>
.ai-font {
  background: linear-gradient(135deg, #394BA7 0, #96C064 33%, #EC6822 100%);
  display: -webkit-inline-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  word-break: break-word;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  inline-size: -webkit-fit-content;
  inline-size: fit-content;
}

.sidebar-content {
  flex-grow: 1;
  overflow-y: auto;
}

.sidebar-footer {
  padding: 10px;
  border-top: 1px solid #ddd;
  background: white;
  justify-content: end;
}

:deep(.p-inputtext) {
  height: 60px;
  border-radius: 16px;
}

.no-resize {
  resize: none;
}

.custom-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 20px;
}

.list-icon {
  font-size: 1.5rem;
  color: #4a90e2;
}

.list-text-main {
  font-size: 1rem;
}

.list-text-sub {
  font-size: 0.8rem;
}

:deep(.p-listbox.custom-listbox) {
  border: none;
}

:deep(.p-listbox.p-listbox-list.p-listbox-item.custom-item) {
  background-color: #f0f4f9 !important;
  border: 2px solid white !important;
}

span.icon {
  background-color: #f1f1f1;
  border-radius: 99px;
  width: 30px;
  height: 30px;
  justify-content: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.gradient-skeleton {
  background: linear-gradient(135deg, #6e7ab6 0, #a5b98d 83%);
  opacity: 0.4;
}

.loading-small-text {
  color: #313131;
}

.chat-threads, .user-prompts {
  display: flex;
  flex-direction: column;
}

.ai-bot-response {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
}

:deep(code) {
  color: #5e5e5e !important;
  background-color: #e7e7e7;
  padding: 2px;
}

:deep(pre) {
  overflow-x: auto !important;
  overflow-y: hidden !important;
  padding-top: 10px;
  padding-bottom: 10px;
  display: flex;
}

:deep(ul) {
  list-style: disc;
}

:deep(li) {
  margin-bottom: 10px;
}

.sidebar-content {
  padding: 1.1rem;
}

.resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  width: 2px;
  height: 100%;
  cursor: ew-resize;
  background: rgba(0, 0, 0, 0.3);
  transition: background 0.2s ease-in-out;
}

.resize-handle:hover {
  background: rgba(0, 0, 0, 0.2);
}

.no-select,
.no-select * {
  user-select: none;
  pointer-events: none;
}

</style>